import { Button, TextField, Link, ValidationMessage } from '@huntington/honeycomb';
import { Box, Typography } from '@mui/material';
import {
	VERIFY_OTP_TITLE,
	VERIFY_OTP_DESCRIPTION,
	VERIFY_OTP_CONTINUE_BUTTON,
	VERIFY_OTP_INPUT_LABEL,
	VERIFY_OTP_INPUT_PLACEHOLDER,
	VERIFY_OTP_CODE_ERROR,
	NEW_OTP_CODE,
	API_ERROR_PAGE_FAILED_TO_SEND_CODE,
} from '../../../constants/messages';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGenerateOTPMutation, useValidateOTPMutation } from '../../../api/user.api';
import { useFormHook } from '../../../hooks/useFormHook';
import { ROUTE_ENROLLMENT, ROUTE_ERROR, ROUTE_CREATE_PASSWORD } from '../../../router/router';
import { ColorBaseCharcoal } from '@huntington/honeycomb-design';

// Held at the backend rate-limit cap (50/day) until Ted's UX rework re-tightens it.
export const MAX_ATTEMPTS = 50;

export default function VerifyOtp() {
	const [error, setError] = useState('');
	const [otp, setOtp] = useState('');
	const [attempts, setAttempts] = useState(0);
	const location = useLocation();
	const email = location.state?.email || '';
	const [generateOtp] = useGenerateOTPMutation();
	const [validateOtp] = useValidateOTPMutation();
	const { validate } = useFormHook();
	const navigate = useNavigate();

	const handleContinue = async () => {
		// Validates that OTP is a 6-digit number and not empty
		if (validate.isInvalidString(otp) || otp.length !== 6) {
			setError(VERIFY_OTP_CODE_ERROR);
			return;
		}

		// Ensures email is available from navigation state
		if (!email) {
			setError(VERIFY_OTP_CODE_ERROR);
			return;
		}

		// Clear any previous errors before validation
		setError('');

		// Validate OTP with the backend. If invalid, increment the number of attempts and show error message.
		// If valid, navigate to Create Password page with reset token.
		try {
			const response = await validateOtp({ emailId: email, otp }).unwrap();

			navigate(`${ROUTE_ENROLLMENT}/${ROUTE_CREATE_PASSWORD}`, {
				state: { email, resetToken: response.resetToken },
			});
		} catch (err: any) {
			const errorMessage = err?.data?.message || err?.data?.causeMessage || '';

			if (errorMessage.toLowerCase().includes('expired')) {
				navigate(`${ROUTE_ENROLLMENT}/${ROUTE_ERROR}`);
				return;
			} else if (err.status === 500) {
				// If we've hit too many attempts, include email state; otherwise just redirect
				if (attempts >= MAX_ATTEMPTS) {
					navigate(`${ROUTE_ENROLLMENT}/${ROUTE_ERROR}`, { state: { email } });
				} else {
					navigate(`${ROUTE_ENROLLMENT}/${ROUTE_ERROR}`);
				}
				return;
			} else if (err.status === 400) {
				setAttempts((prev) => prev + 1);
				setError(VERIFY_OTP_CODE_ERROR);
			} else {
				setError(VERIFY_OTP_CODE_ERROR);
			}
		}
	};

	const handleGetNewCode = async () => {
		if (!email) {
			setError(VERIFY_OTP_CODE_ERROR);
			return;
		}

		setError('');

		try {
			await generateOtp({ emailId: email }).unwrap();
		} catch (err: any) {
			// Backend now returns 200 for /generate-otp regardless of email existence to prevent account enumeration (#2795511),
			// so 404 is no longer reachable. Treat all failures (500 / network) as a generic "failed to send code" message.
			if (err.status === 500 && attempts >= MAX_ATTEMPTS) {
				setError(VERIFY_OTP_CODE_ERROR);
			} else {
				setError(API_ERROR_PAGE_FAILED_TO_SEND_CODE);
			}
		}
	};

	return (
		<Box>
			<Typography
				variant="h1"
				component="h1"
				sx={{ font: 'bold 2.25rem "Huntington Serif", serif', marginBottom: '1.5rem' }}
			>
				{VERIFY_OTP_TITLE}
			</Typography>

			<Box>
				<Typography gutterBottom sx={{ paddingBottom: '1rem' }}>
					{VERIFY_OTP_DESCRIPTION}
				</Typography>

				<TextField
					label={VERIFY_OTP_INPUT_LABEL}
					placeholder={VERIFY_OTP_INPUT_PLACEHOLDER}
					id="verification-code"
					name="verificationCode"
					type="text"
					value={otp}
					error={!!error}
					aria-describedby={error ? 'validation-message' : undefined}
					onChange={(e) => {
						const value = e.target.value;
						if (/^\d*$/.test(value) && value.length <= 6) {
							setOtp(value);
						}
					}}
					inputProps={{
						maxLength: 6,
						inputMode: 'numeric',
						pattern: '[0-9]*',
					}}
					onBlur={() => {
						// Fires error if user tabs away without entering a valid 6-digit OTP
						if (validate.isInvalidString(otp)) {
							setError(VERIFY_OTP_CODE_ERROR);
						}
					}}
				/>

				{error && (
					<ValidationMessage
						id="validation-message"
						sx={{ mt: '0.25rem' }}
						role="alert"
						aria-live="assertive"
					>
						<span>{error}</span>
					</ValidationMessage>
				)}

				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'flex-end',
						marginTop: '1.5rem',
						alignSelf: 'stretch',
					}}
				>
					<Link
						sx={{ cursor: 'pointer', ':hover': { color: ColorBaseCharcoal } }}
						onClick={handleGetNewCode}
					>
						{NEW_OTP_CODE}
					</Link>
					<Button onClick={handleContinue}>{VERIFY_OTP_CONTINUE_BUTTON}</Button>
				</Box>
			</Box>
		</Box>
	);
}
